import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Shield, X, Send, Unlock, ScrollText, Lock, Trash2, Radio, RefreshCw } from "lucide-react";

/**
 * Admin moderation panel — visible only to users with `is_admin: true`.
 * Grants ticket-answering, unmute, ad-hoc mute, and a scrolling log
 * of recent AI-moderator decisions.
 */
export default function AdminPanel({ onClose }) {
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [actions, setActions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [muteUid, setMuteUid] = useState("");
  const [muteMin, setMuteMin] = useState("");
  const [tab, setTab] = useState("tickets"); // tickets | actions | tools | users
  const [users, setUsers] = useState(null);
  // Discord Mod Log bridge — GM controls (settings toggle + backfill).
  const [modlogSettings, setModlogSettings] = useState(null);
  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillHistory, setBackfillHistory] = useState([]);

  const refreshModlogSettings = async () => {
    try { setModlogSettings(await api.modlogSettings()); }
    catch { /* silent */ }
  };
  const refreshBackfillHistory = async () => {
    try {
      const r = await api.modlogBackfillHistory(10);
      setBackfillHistory(r?.history || []);
    } catch { /* silent */ }
  };
  const toggleAiForward = async () => {
    if (!modlogSettings) return;
    const next = !modlogSettings.ai_forward_enabled;
    try {
      const r = await api.modlogSetSettings(next);
      setModlogSettings(r);
      toast.success(next ? "AI forward attivato" : "AI forward disattivato");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impostazioni fallite");
    }
  };
  const runBackfill = async () => {
    if (backfillBusy) return;
    setBackfillBusy(true);
    try {
      const r = await api.modlogBackfill(50);
      toast.success(`Backfill: ${r.sent} inviati · ${r.failed} falliti · ${r.skipped} saltati`);
      await Promise.all([refresh(), refreshBackfillHistory()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill fallito");
    } finally { setBackfillBusy(false); }
  };

  const refreshUsers = async () => {
    try { setUsers(await api.adminUsers()); } catch (e) { /* silent */ }
  };

  const deleteUser = async (user) => {
    if (busy || user.is_self || user.character?.is_admin) return;
    if (!window.confirm(`Delete registered user ${user.email}? This removes their character and gameplay data.`)) return;
    setBusy(true);
    try { await api.adminDeleteUser(user.user_id); toast.success("User deleted"); await refreshUsers(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setBusy(false); }
  };

  useEffect(() => {
    if (tab === "users") {
      refreshUsers();
      const t = setInterval(refreshUsers, 15000);
      return () => clearInterval(t);
    }
  }, [tab]);

  useEffect(() => {
    if (tab === "actions") {
      refreshModlogSettings();
      refreshBackfillHistory();
    }
  }, [tab]);

  const refresh = async () => {
    try {
      const [t, a] = await Promise.all([api.adminTicketsList(), api.adminModActions(80)]);
      setTickets(t);
      setActions(a);
      if (selected) setSelected(t.find(x => x.ticket_id === selected.ticket_id) || null);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { refresh(); const t = setInterval(refresh, 8000); return () => clearInterval(t); }, []);

  const sendReply = async () => {
    if (!reply.trim() || busy || !selected) return;
    setBusy(true);
    try {
      await api.ticketReply(selected.ticket_id, reply.trim());
      setReply("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const closeTicket = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.adminTicketClose(selected.ticket_id);
      toast.success("Ticket closed");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const unmuteUser = async (uid) => {
    setBusy(true);
    try {
      await api.adminUnmute(uid);
      toast.success("User unmuted");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const muteUser = async () => {
    if (!muteUid.trim()) return;
    setBusy(true);
    try {
      const minutes = muteMin ? parseInt(muteMin, 10) : null;
      await api.adminMute(muteUid.trim(), minutes, "admin action");
      toast.success(minutes ? `Muted for ${minutes}m` : "Muted permanently");
      setMuteUid(""); setMuteMin("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const clearChat = async (channel) => {
    if (busy) return;
    if (!window.confirm(`Delete all messages from the ${channel} chat(s)?`)) return;
    setBusy(true);
    try {
      const r = await api.adminClearChat(channel);
      toast.success(`Cleared ${r.deleted} messages (${channel})`);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-40 bg-black/80 backdrop-blur-sm flex items-stretch justify-end"
      data-testid="admin-panel"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl bg-[#050505] border-l border-[#262626] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-[#262626] flex items-center gap-2 sticky top-0 bg-[#050505] z-10">
          <Shield className="w-4 h-4 text-[#E5C34A]" strokeWidth={1.5} />
          <span className="chat-admin-name-text font-heading text-sm tracking-[0.3em] uppercase">
            Warden Console
          </span>
          <a
            href="/admin/discord"
            data-testid="admin-discord-link"
            className="ml-auto mr-2 font-heading text-[10px] tracking-widest uppercase text-[#5865F2] hover:text-[#8FA5FF] border border-[#5865F2]/40 hover:border-[#5865F2] px-2 py-1"
          >
            Gestione Discord
          </a>
          <button
            data-testid="admin-close"
            onClick={onClose}
            className="text-[#666] hover:text-[#E2DFD8]"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>

        <div className="flex overflow-x-auto border-b border-[#262626]">
          {[
            { id: "tickets", label: `Tickets (${tickets.filter(t => t.status !== "closed").length})` },
            { id: "actions", label: "Mod Log" },
            { id: "tools", label: "Tools" },
            { id: "users", label: "Users" },
          ].map(t => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`min-w-max flex-1 py-2 px-2 font-heading text-[10px] tracking-widest uppercase ${
                tab === t.id ? "text-[#E5C34A] border-b-2 border-[#E55A00]" : "text-[#666]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "tickets" && (
          <div className="grid grid-cols-2 gap-2 p-2" data-testid="admin-tickets">
            <div className="panel max-h-[70vh] overflow-y-auto">
              {tickets.length === 0 && <p className="italic text-[#666] text-xs p-3">No tickets.</p>}
              {tickets.map(t => {
                const isSel = selected?.ticket_id === t.ticket_id;
                return (
                  <button
                    key={t.ticket_id}
                    data-testid={`admin-ticket-${t.ticket_id}`}
                    onClick={() => setSelected(t)}
                    className={`w-full text-left px-3 py-2 border-b border-[#1a1a1a] hover:bg-[#0e0e0e] ${isSel ? "bg-[#0e0e0e]" : ""}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-body text-xs text-[#E2DFD8] truncate flex-1">{t.subject}</span>
                      <span className={`font-mono text-[9px] tracking-widest uppercase ${
                        t.status === "closed" ? "text-[#666]" :
                        t.status === "answered" ? "text-[#6E8B3D]" : "text-[#B89C3A]"
                      }`}>{t.status}</span>
                    </div>
                    <div className="font-mono text-[9px] text-[#555] mt-0.5">
                      {t.user_name} · {t.messages.length} msg
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="panel">
              {selected ? (
                <>
                  <div className="px-3 py-2 border-b border-[#262626]">
                    <div className="font-heading text-xs tracking-widest uppercase text-[#E2DFD8] truncate">
                      {selected.subject}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <span className="font-mono text-[9px] text-[#666]">user_id: {selected.user_id}</span>
                      <button
                        data-testid="admin-unmute-from-ticket"
                        onClick={() => unmuteUser(selected.user_id)}
                        disabled={busy}
                        className="ml-auto flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase text-[#6E8B3D] hover:text-[#B0D060]"
                      >
                        <Unlock className="w-3 h-3" strokeWidth={1.5} /> Unmute
                      </button>
                      <button
                        data-testid="admin-close-ticket"
                        onClick={closeTicket}
                        disabled={busy}
                        className="text-[10px] font-heading tracking-widest uppercase text-[#8A0303] hover:text-[#E33333]"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[55vh] overflow-y-auto p-3 space-y-2">
                    {selected.messages.map((m, i) => (
                      <div key={i} className={`text-xs p-2 border-l-2 ${m.from === "admin" ? "border-[#B89C3A] bg-[#0a0a0a]" : "border-[#262626]"}`}>
                        <div className="font-heading text-[10px] tracking-widest uppercase mb-1"
                          style={{ color: m.from === "admin" ? "#E5C34A" : "#4A7BC7" }}>
                          {m.from} · {m.author} · {new Date(m.at).toLocaleString()}
                        </div>
                        <div className="text-[#E2DFD8]">{m.body}</div>
                      </div>
                    ))}
                  </div>
                  {selected.status !== "closed" && (
                    <div className="flex border-t border-[#262626]">
                      <input
                        data-testid="admin-reply-input"
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendReply()}
                        placeholder="Admin reply..."
                        maxLength={2000}
                        className="flex-1 bg-transparent px-3 py-2 text-xs text-[#E2DFD8] outline-none placeholder-[#555]"
                      />
                      <button
                        data-testid="admin-reply-send"
                        onClick={sendReply}
                        disabled={busy || !reply.trim()}
                        className="px-3 border-l border-[#262626] text-[#E5C34A] hover:bg-[#1a1a1a] disabled:opacity-30"
                      >
                        <Send className="w-3 h-3" strokeWidth={1.5} />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <p className="italic text-[#666] text-xs p-3">Select a ticket to reply.</p>
              )}
            </div>
          </div>
        )}

        {tab === "actions" && (
          <div className="panel m-2 max-h-[75vh] overflow-y-auto" data-testid="admin-actions-log">
            <div className="px-3 py-2 border-b border-[#262626] flex items-center gap-2">
              <ScrollText className="w-3 h-3 text-[#B89C3A]" strokeWidth={1.5} />
              <span className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">
                Recent Moderator Decisions
              </span>
            </div>
            {/* Discord ModLog bridge controls */}
            <div className="px-3 py-2 border-b border-[#262626] bg-[#0a0a0a]" data-testid="modlog-bridge-controls">
              <div className="flex items-center gap-2 mb-2">
                <Radio className="w-3 h-3 text-[#5D2F5F]" strokeWidth={1.5} />
                <span className="font-heading text-[9px] tracking-widest uppercase text-[#8c8c8c]">
                  Discord Mod Log Bridge
                </span>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  data-testid="modlog-ai-forward-toggle"
                  onClick={toggleAiForward}
                  disabled={!modlogSettings}
                  className={`flex items-center gap-2 px-2 py-1 border font-mono text-[10px] uppercase tracking-widest transition ${
                    modlogSettings?.ai_forward_enabled
                      ? "border-[#6E8B3D] text-[#B0D060] hover:bg-[#0e0e0e]"
                      : "border-[#8A0303] text-[#E33333] hover:bg-[#0e0e0e]"
                  } disabled:opacity-30`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${modlogSettings?.ai_forward_enabled ? "bg-[#B0D060]" : "bg-[#E33333]"}`} />
                  AI forward: {modlogSettings ? (modlogSettings.ai_forward_enabled ? "ON" : "OFF") : "…"}
                </button>
                <button
                  data-testid="modlog-backfill-btn"
                  onClick={runBackfill}
                  disabled={backfillBusy}
                  className="flex items-center gap-2 px-2 py-1 border border-[#B89C3A] text-[#E5C34A] font-mono text-[10px] uppercase tracking-widest hover:bg-[#0e0e0e] disabled:opacity-30"
                >
                  <RefreshCw className={`w-3 h-3 ${backfillBusy ? "animate-spin" : ""}`} strokeWidth={1.5} />
                  {backfillBusy ? "Backfilling…" : "Backfill 50"}
                </button>
                <span className="font-mono text-[9px] text-[#666]">
                  toggle disables AI verdicts (warn/mute/permaban/throttled); admin actions always forward
                </span>
              </div>
              {/* Backfill history — small rolling log so GMs see webhook stability at a glance */}
              <div className="mt-2 pt-2 border-t border-[#1a1a1a]" data-testid="modlog-backfill-history">
                <div className="font-heading text-[9px] tracking-widest uppercase text-[#666] mb-1">
                  Backfill history (last {backfillHistory.length})
                </div>
                {backfillHistory.length === 0 ? (
                  <p className="italic text-[9px] text-[#555]">Nessun backfill eseguito ancora.</p>
                ) : (
                  <ul className="space-y-0.5 max-h-32 overflow-y-auto">
                    {backfillHistory.map((h) => {
                      const okAll = h.failed === 0 && h.processed > 0;
                      const noop  = h.processed === 0;
                      const failing = h.failed > 0;
                      const dotColor = failing ? "bg-[#E33333]" : (noop ? "bg-[#8c8c8c]" : "bg-[#B0D060]");
                      return (
                        <li key={h.run_id} className="flex items-center gap-2 font-mono text-[9px]">
                          <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />
                          <span className="text-[#8c8c8c]">{new Date(h.at).toLocaleString()}</span>
                          <span className="text-[#E2DFD8]">{h.admin_name || "admin"}</span>
                          <span className="text-[#B0D060]">·{h.sent} sent</span>
                          {h.failed > 0 && <span className="text-[#E33333]">·{h.failed} fail</span>}
                          {h.skipped > 0 && <span className="text-[#B89C3A]">·{h.skipped} skip</span>}
                          <span className="ml-auto text-[#555]">limit {h.limit}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
            {actions.length === 0 && (
              <p className="italic text-[#666] text-xs p-3">Nothing yet.</p>
            )}
            {actions.map(a => (
              <div key={a.action_id} className="border-b border-[#1a1a1a] p-2">
                <div className="flex items-center gap-2 text-[10px] font-mono">
                  <span className={
                    a.action === "warn" ? "text-[#B89C3A]" :
                    a.action === "mute" || a.action === "admin_mute" ? "text-[#8A0303]" :
                    a.action === "permaban" ? "text-[#E33333]" :
                    a.action === "admin_unmute" ? "text-[#6E8B3D]" :
                    "text-[#8c8c8c]"
                  }>
                    {a.action.toUpperCase()}
                  </span>
                  <span className="text-[#8c8c8c] uppercase">[{a.channel}]</span>
                  <span className="text-[#E2DFD8] font-heading tracking-widest">{a.user_name}</span>
                  <span className="ml-auto text-[#555]">{new Date(a.created_at).toLocaleTimeString()}</span>
                </div>
                {a.text && (
                  <div className="text-xs text-[#B0ADA5] mt-1 italic">"{a.text}"</div>
                )}
                {a.verdict?.reason && (
                  <div className="text-[10px] text-[#8c8c8c] mt-1">→ {a.verdict.reason}</div>
                )}
                {a.action !== "admin_unmute" && a.action !== "allow" && (
                  <button
                    data-testid={`admin-unmute-${a.user_id}`}
                    onClick={() => unmuteUser(a.user_id)}
                    className="mt-1 text-[9px] font-heading tracking-widest uppercase text-[#6E8B3D] hover:text-[#B0D060] flex items-center gap-1"
                  >
                    <Unlock className="w-2.5 h-2.5" strokeWidth={1.5} /> Unmute this user
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "tools" && (
          <div className="panel m-2 p-3 space-y-3" data-testid="admin-tools">
            <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c] flex items-center gap-2">
              <Lock className="w-3 h-3 text-[#8A0303]" strokeWidth={1.5} />
              Ad-hoc Mute
            </div>
            <input
              data-testid="admin-mute-uid"
              value={muteUid}
              onChange={(e) => setMuteUid(e.target.value)}
              placeholder="user_id"
              className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1.5 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A]"
            />
            <input
              data-testid="admin-mute-min"
              value={muteMin}
              onChange={(e) => setMuteMin(e.target.value)}
              placeholder="minutes (leave blank = permanent)"
              type="number"
              className="w-full bg-[#0a0a0a] border border-[#262626] px-2 py-1.5 text-xs text-[#E2DFD8] outline-none focus:border-[#B89C3A]"
            />
            <button
              data-testid="admin-mute-submit"
              onClick={muteUser}
              disabled={busy || !muteUid.trim()}
              className="w-full py-2 border border-[#8A0303] text-[#E33333] font-heading text-xs tracking-widest uppercase disabled:opacity-30"
            >
              Apply Mute
            </button>

            <div className="pt-4 border-t border-[#262626] mt-4 space-y-2" data-testid="admin-clear-chat">
              <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">
                Clear Chat (visible to everyone)
              </div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="admin-clear-global"
                  onClick={() => clearChat("global")}
                  disabled={busy}
                  className="py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase hover:bg-[#B89C3A]/10 disabled:opacity-30"
                >
                  Clear Global
                </button>
                <button
                  data-testid="admin-clear-hub"
                  onClick={() => clearChat("hub")}
                  disabled={busy}
                  className="py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase hover:bg-[#B89C3A]/10 disabled:opacity-30"
                >
                  Clear Hub
                </button>
                <button
                  data-testid="admin-clear-guild"
                  onClick={() => clearChat("guild")}
                  disabled={busy}
                  className="py-2 border border-[#B89C3A] text-[#E5C34A] font-heading text-[10px] tracking-widest uppercase hover:bg-[#B89C3A]/10 disabled:opacity-30"
                >
                  Clear Guild
                </button>
                <button
                  data-testid="admin-clear-all"
                  onClick={() => clearChat("all")}
                  disabled={busy}
                  className="py-2 border border-[#8A0303] text-[#E33333] font-heading text-[10px] tracking-widest uppercase hover:bg-[#8A0303]/10 disabled:opacity-30"
                >
                  Clear ALL Chats
                </button>
              </div>
            </div>
          </div>
        )}



        {tab === "users" && (
          <div className="panel m-2 p-3 space-y-3 max-h-[75vh] overflow-y-auto" data-testid="admin-users">
            <div className="flex items-center justify-between border-b border-[#262626] pb-2">
              <div className="font-heading text-[10px] tracking-widest uppercase text-[#8c8c8c]">Registered Users</div>
              <button onClick={refreshUsers} className="font-mono text-[9px] uppercase text-[#B89C3A]">Refresh</button>
            </div>
            {!users && <div className="text-center text-xs italic text-[#666] py-3">Loading…</div>}
            <div className="space-y-1">
              {users?.users?.map(u => (
                <div key={u.user_id} className="flex items-center gap-2 p-2 border border-[#262626]">
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[10px] text-[#E5C34A] truncate">{u.email}</div>
                    <div className="font-mono text-[9px] text-[#8c8c8c] truncate">{u.character?.name || u.name || "—"} · {u.character?.chosen_class || "Not onboarded"} · Lv{u.character?.level || 1}</div>
                  </div>
                  <span className="font-mono text-[8px] text-[#555]">{u.created_at ? new Date(u.created_at).toLocaleDateString() : ""}</span>
                  {u.is_self || u.character?.is_admin ? (
                    <span className="font-mono text-[8px] uppercase text-[#6E8B3D]">Protected</span>
                  ) : (
                    <button data-testid={`admin-delete-user-${u.user_id}`} onClick={() => deleteUser(u)} disabled={busy} className="px-2 py-1 border border-[#8A0303] text-[#E33333] font-mono text-[9px] uppercase disabled:opacity-30"><Trash2 className="w-3 h-3 inline mr-1" />Delete</button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
